import { useState, useEffect } from 'react'
import { Lock, Loader2, Send } from 'lucide-react'
import { getInternalNotes, addInternalNote, type InternalNote } from '../../lib/feedbackApi'
import { formatRelativeTime } from '../../lib/format'

interface AdminInternalNotesProps {
  feedbackId: number
  isAdmin: boolean
}

export function AdminInternalNotes({ feedbackId, isAdmin }: AdminInternalNotesProps) {
  const [notes, setNotes] = useState<InternalNote[]>([])
  const [loading, setLoading] = useState(true)
  const [body, setBody] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // Fetch notes
  useEffect(() => {
    if (!isAdmin) return
    setLoading(true)
    getInternalNotes(feedbackId)
      .then(setNotes)
      .catch(() => setNotes([]))
      .finally(() => setLoading(false))
  }, [feedbackId, isAdmin])

  if (!isAdmin) return null

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const trimmed = body.trim()
    if (!trimmed || submitting) return

    setSubmitting(true)
    try {
      const note = await addInternalNote(feedbackId, trimmed)
      setNotes((prev) => [...prev, note])
      setBody('')
    } catch {
      // Failed silently
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="mt-4">
      <div className="flex items-center gap-2 mb-3">
        <Lock className="w-3.5 h-3.5 text-amber-500" />
        <h4 className="text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wide">
          Internal Notes
        </h4>
      </div>

      <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3 space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          </div>
        ) : notes.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">No internal notes yet</p>
        ) : (
          <div className="space-y-2.5">
            {notes.map((note) => (
              <div key={note.id} className="text-sm">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-medium text-foreground text-xs">{note.admin_name}</span>
                  <span className="text-[10px] text-muted-foreground">
                    {formatRelativeTime(note.created_at)}
                  </span>
                </div>
                <p className="text-xs text-foreground/80 whitespace-pre-wrap">{note.body}</p>
              </div>
            ))}
          </div>
        )}

        {/* Add note form */}
        <form onSubmit={handleSubmit} className="flex gap-2">
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Add an internal note..."
            rows={2}
            className="flex-1 text-xs bg-card border border-border rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground"
          />
          <button
            type="submit"
            disabled={!body.trim() || submitting}
            className="self-end px-3 py-2 bg-amber-500/20 text-amber-600 dark:text-amber-400 rounded-lg text-xs font-medium hover:bg-amber-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          </button>
        </form>
      </div>
    </div>
  )
}
